import Backbone from 'backbone'
import DraggableList from 'draggable-list'
import $ from 'jquery'
import _ from 'lodash'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import appDisplay from 'scoreboard/components/app-display'
import i18n from 'scoreboard/components/i18n'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import selects from 'scoreboard/components/selects'
import Sorter from 'scoreboard/components/sorter'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'
import renderValuesSelect from 'scoreboard/datasets/filters/render-values-select'
import notInAnyRangeMessage from 'scoreboard/datasets/not-in-any-range-message'
import { NOT_IN_ANY } from 'scoreboard/datasets/overview/datasets-tabular-result'
import Dataset from 'scoreboard/datasets/model/dataset'
const { BLANK } = Dataset
import wrapModel from 'scoreboard/components/wrap-model'
import ChartDesignerRange from '../model/chart-designer-range'
import ChartDesignerBreakdown from '../model/chart-designer-breakdown'
import blankMessage from '../blank-message'
import axesText from './axes-text'
import SortObjectTypes from './sort/sort-object-types'
const { BREAKDOWN1, BREAKDOWN2 } = SortObjectTypes
import BreakdownRangeSortSettingsView from './sort/breakdown-range-sort-settings-view'
import BreakdownRangeView from './breakdown-range-view'
import template from './breakdown-view.hbs'
import ChartsEditCalendarPeriodRangeView from './charts-edit-calendar-period-range-view'
import EditBreakdownView from './edit-breakdown-view'
import updatesNeeded from './updates-needed'
import nonAxesPlotType from 'scoreboard/charts/non-axes-plot-type'
import { chart as isPolarChart } from 'scoreboard/charts/is-polar'
import rangeKeySuffix from 'scoreboard/datasets/edit/range-key-suffix'
import icon from 'scoreboard/components/icon'

const BreakdownView = Backbone.View.extend({

	className: 'breakdown-view',

	/**
	 * @type ChartDesignerBreakdown
	 */
	model: null,

	/**
	 * @type ChartDesigner
	 */
	chart: null,

	/** @type {ChartData} */
	data: null,

	events: {
		'click .delete-breakdown': 'onClickDelete',
		'click .edit-breakdown': 'onClickEdit',
		'click .add-range-button': 'onClickAddRange',
		'click .swap-breakdown': 'onClickSwap'
	},

	initialize: function(options = {}) {
		this.options = options
		this.chart = options.chart
		this.data = options.data

		this.paint = _.debounce(this.paint.bind(this), 0)

		this.listenTo(this.model, 'change', () => this.onBreakdownUpdate())
		this.listenTo(this.chart.features, 'change add remove', () => this.paint())

		this.rangeViews = []
	},

	render: function() {
		this.$el.empty().append(template())
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		this.paint()

		return this
	},

	setAddRangeLabel: function() {
		const breakdownBy = this.model.get('breakdownBy')
		const dataset = this.chart.data.datasets()[this.model.get('datasetId')]
		let addRangeLabel = i18n.get('charts.breakdown.addRange.numeric')
		if (dataset) {
			let datasetModel = wrapModel(dataset, Dataset)
			const field = datasetModel.combinedFieldsById[this.model.get('datasetFieldId')]

			// Handles breakdownBy = calendar-period-ranges  OR calendar-period-grouped-ranges
			switch (breakdownBy) {
				case 'specific-values':
				case 'every-value':
					addRangeLabel = i18n.get('charts.breakdown.addSpecificValue', field.label)
					break
				case 'specific-ranges':
				case 'every-range':
					const suffix = rangeKeySuffix(field.dataType)
					addRangeLabel = i18n.get(`charts.breakdown.addRange${suffix}`)
					break
				default:
					// Handles breakdownBy = calendar-period-ranges and calendar-period-grouped-ranges
					addRangeLabel = i18n.get('charts.breakdown.addRange.numeric')
			}
		}

		this.$('.add-range-button')

			.html(icon('add'))
			.append(addRangeLabel)
	},

	/**
	 * Updates the UI with the latest breakdown config; works either on initial
	 * render or on update.
	 *
	 * Debounced up in initialize() so that a series of changes doesn't trigger
	 * a paint in a half-changed state (possibly causing errors)
	 */
	paint: function () {
		const axisTextKey = nonAxesPlotType(this.chart) || isPolarChart(this.chart) ?
			'charts.chart.yAxis.data' : axesText(this.chart)
		this.$('.breakdown-axes').text(i18n.get(axisTextKey))
		let breakdownLabel = this.chart.breakdownLabel(this.model)
		this.$('.breakdown-label').text(breakdownLabel)

		this.toggleAddRangeButton(true)
		this.toggleAddDatasetRangeInput(false)

		this.renderSortSettings()

		let rangeList = this.$('.breakdown-ranges-list')

		this.removeRangeViews()
		this.model.ranges.ordered().forEach(range => {
			let view = new BreakdownRangeView({
				model: range,
				breakdown: this.model,
				chartDesigner: this.chart,
				data: this.data
			})
			this.rangeViews.push(view)

			rangeList.append(view.el)

			view.render()
		})

		this.renderEditDeleteButtons()
		this.renderErrorState()
		this.renderRangeDraggableList()
		this.$('.breakdown-range-message .message')
			.text(i18n.get('charts.chart.addRange', breakdownLabel))
		this.$('.breakdown-ranges').toggleClass('is-empty', !this.model.ranges.length)

		// Items that can have variable heights need to have a fixed height value
		// so that transitions can work properly. We can easily achieve this by
		// grabbing the hidden `.active-content` height on load. Because the inactive
		// state shows for all panels on load, we don't have to do anything fancy
		// to get the heights like waiting to reveal content while calculating heights.
		// Add 32px for the "Sorting" section
		let hiddenContentHeight = this.$('.breakdown-content')[0].clientHeight + 32
		this.$el.height(hiddenContentHeight)
		this.setAddRangeLabel()
	},

	onBreakdownUpdate: function () {
		let results = updatesNeeded(this.model.attributes, this.model.previousAttributes(), {
			nested: {
				ranges: {
					// If the ranges were just reordered by the draggable-list,
					// there's no need to update the view again, because the
					// 'change' event listener on ChartDesigner in charts-edit.js
					// will take care of it
					noRerender: [ 'id', 'sortOrder' ]
				}
			}
		})

		if (results.rerender) {
			this.paint()
		}
	},

	renderEditDeleteButtons: function () {
		// If there's a scorecard source, there always has to be one breakdown,
		// so we can't delete it
		// #40345 the delete button should still show if this breakdown is not
		// a calendar breakdown (in other words, it is a dataset field breakdown)
		const cannotDelete = this.chart.hasScorecardItemSources() &&
			this.model.isCalendarPeriodBreakdown()
		this.$('.delete-breakdown')
			.toggleClass('is-not-displayed', cannotDelete)

		// Breakdowns can't be edited unless there's a dataset series, because
		// there isn't anything to change at the breakdown level for non-dataset
		// breakdowns (changing between standard and grouped calendar period
		// breakdowns is done in the Edit Range dialog)
		this.$('.edit-breakdown').toggleClass('is-not-displayed',
			!this.chart.hasDatasetSources())
	},

	renderErrorState: function () {
		let error = this.model.get('error')
		this.$el.toggleClass('has-error', !!error)

		this.errorTooltip && this.errorTooltip.remove()

		if (error) {
			this.errorTooltip = new TooltipAndDetails({
				el: this.$('.error-display'),
				selector: 'svg',
				tooltipContent: () => {
					return `<div>${_.escape(error.message)}</div>`
				},
				tooltipOptions: {
					extraClass: 'breakdown-error-tooltip',
					placement: 'above'
				}
			})
		}
	},

	renderSortSettings: function () {
		if (!this.sortSettingsView) {
			this.sortSettingsView = new BreakdownRangeSortSettingsView({
				sortObjectType: this.model.get('sortOrder') == 0 ? BREAKDOWN1 : BREAKDOWN2,
				model: this.model.sortSettings,
				chart: this.chart,
				breakdown: this.model,
				el: this.$('.sort-settings')
			})

			this.listenTo(this.sortSettingsView, 'change', () => this.trigger('change:sortSettings'))
		}

		this.sortSettingsView.render()
	},

	renderRangeDraggableList: function () {
		this.rangeDraggableList = new DraggableList(this.$('.breakdown-ranges-list')[0])
		this.rangeDraggableList.on('move', (movedEl, newIndex, prevIndex) => {
			this.model.reorderRanges({
				newIndex,
				prevIndex,
				data: this.data
			})
		})
	},

	/**
	 * Shows or hides the Add Range button; includes logic to ignore a request
	 * to show the button if it shouldn't be allowed (see addRangeAllowed())
	 *
	 * @param {Boolean} show
	 */
	toggleAddRangeButton: function (show) {
		this.$('.add-range-button').toggleClass('is-not-displayed',
			!(show && this.addRangeAllowed()))
	},

	/**
	 * Shows or hides the filtering select to add a new breakdown range based on
	 * a dataset field; includes logic to ignore a request to show the input if
	 * it shouldn't be allowed (see addRangeAllowed())
	 *
	 * @param {Boolean} show
	 */
	toggleAddDatasetRangeInput: function (show) {
		let breakdownBy = this.model.get('breakdownBy')

		if (show && this.addRangeAllowed()) {
			let input = $('<select class="add-range-input"></select>')
				.insertBefore(this.$('.breakdown-ranges-list'))

			if (breakdownBy === 'specific-values' || breakdownBy === 'specific-ranges') {
				if (breakdownBy === 'specific-values') {
					this.renderDatasetValueSelect(input)
				} else if (breakdownBy === 'specific-ranges') {
					this.renderDatasetRangeSelect(input)
				}

				let textarea = this.$('.add-range-input textarea')
					.focus()
					.on('blur', () => {
						this.toggleAddRangeButton(true)
						this.toggleAddDatasetRangeInput(false)
					})

				input.on('change', () => {
					// toggleAddDatasetRangeInput() re-renders the select, but that causes
					// a problem if it's in the middle of a change event. So do a
					// nextTick() before processing the change
					process.nextTick(() => {
						if (breakdownBy === 'specific-values') {
							this.addDatasetValueRange(input.val()[0])
						} else if (breakdownBy === 'specific-ranges') {
							if (input.val()) {
								let val = input.val()

								this.addDatasetRangeRange(val)
							}
						}

						this.toggleAddRangeButton(true)
						this.toggleAddDatasetRangeInput(false)
					})
				})
			}
		} else {
			this.$('.add-range-input').remove()
		}
	},

	/**
	 * Renders a filtering select of available dataset values that can be added
	 * as breakdown ranges
	 */
	renderDatasetValueSelect: function (selectEl) {
		let suggestions = this.availableRangeValues()

		let dataset = this.data.datasetModel(this.model.get('datasetId'))
		let dataType = dataset.getUnderlyingField(this.model.get('datasetFieldId')).dataType

		renderValuesSelect({
			dataset: dataset,
			fieldId: this.model.get('datasetFieldId'),
			el: selectEl,
			operator: 'is',
			// Don't send in any pre-selected values, we want it empty
			values: [],
			suggestions,
			sorter: new Sorter({
				alphanumeric: !isNumericDataType(dataType),
				caseSensitive: false
			})
		})
	},

	/**
	 * Renders a filtering select of available dataset ranges that can be added
	 * as breakdown ranges
	 */
	renderDatasetRangeSelect: function (selectEl) {
		let ranges = this.availableRangeRanges()
		ranges.unshift({ id: '', label: '' })

		let defaults = selects.defaults()

		selects(selectEl, {
			open: true,
			options: ranges.map(r => {
				return {
					value: r.id,
					label: r.label
				}
			}),
			render: {
				option: function (d, input) {
					// Add CSS classes for "pseudo ranges" so they can by italicized
					if (d.value === NOT_IN_ANY) {
						return pseudoRangeOption('not-in-any', d.label)
					} else if (d.value === BLANK) {
						return pseudoRangeOption('blank-value', d.label)
					}

					return defaults.render.option(d, input)
				}
			}
		})
	},

	/**
	 * Returns true if adding a breakdown range should be allowed.
	 *
	 * @returns {boolean}
	 */
	addRangeAllowed: function () {
		let breakdownBy = this.model.get('breakdownBy')

		if (breakdownBy === 'specific-values') {
			// If breaking down on specific dataset values, only show the Add Range
			// button if there is at least one possible value left to add (ie
			// don't show if all dataset values are already used)
			return this.availableRangeValues().length > 0
		} else if (breakdownBy === 'specific-ranges') {
			// If breaking down on specific dataset ranges, only show the Add Range
			// button if there is at least one possible range left to add (ie
			// don't show if all dataset ranges are already used)
			return this.availableRangeRanges().length > 0
		} else if (this.model.isCalendarPeriodBreakdown()) {
			// Always allowed to add more calendar period ranges
			return true
		}

		return false
	},

	/**
	 * Returns all valid dataset values that haven't yet been used for a
	 * breakdown range.
	 *
	 * @returns {String[]}
	 */
	availableRangeValues: function () {
		return this.model.get('valueSuggestions').filter(v => {
			return !this.model.ranges.find(r => r.get('value') === v)
		})
	},

	/**
	 * Returns all valid dataset ranges that haven't yet been used for a
	 * breakdown range.
	 *
	 * @returns {Object[]}
	 */
	availableRangeRanges: function () {
		let dataset = this.data.datasetModel(this.model.get('datasetId'))
		let field = dataset.getUnderlyingField(this.model.get('datasetFieldId'))

		let datasetRangesById = _.keyBy(field.ranges, 'id')
		let usedDatasetRangeIds = this.model.ranges
			.filter(r => r.get('type') === 'dataset-range')
			.map(r => {
				let dsRangeType = r.get('datasetRangeType')
				if (dsRangeType === 'standard') {
					return r.get('datasetRangeId')
				} else if (dsRangeType == 'blank') {
					return BLANK
				} else if (dsRangeType == 'not-in-any') {
					return NOT_IN_ANY
				}
			})

		// valueSuggestions will have all the possible rangeIds AND
		// not-in-any (if applicable)
		return this.model.get('valueSuggestions')
			.filter(v => {
				return !usedDatasetRangeIds.find(usedId => usedId == v)
			})
			.map(rangeId => {
				if (rangeId === NOT_IN_ANY) {
					return {
						id: rangeId,
						label: notInAnyRangeMessage(field)
					}
				} else if (rangeId === BLANK) {
					return {
						id: rangeId,
						label: blankMessage()
					}
				} else {
					let numericId = parseInt(rangeId)
					return {
						id: numericId,
						label: datasetRangesById[numericId].label
					}
				}
			})
	},

	/**
	 * Creates a breakdown range based on the given dataset value
	 */
	addDatasetValueRange: function (val) {
		let range = ChartDesignerBreakdown.datasetValueBreakdownRange({
			breakdown: this.model,
			value: val
		})

		this.model.ranges.add(range)
	},

	/**
	 * Creates a breakdown range based on the given dataset range
	 *
	 * @param {String|Int} either a "real" dataset range ID or blank/not-in-any
	 */
	addDatasetRangeRange: function (datasetRangeId) {
		let options = { breakdown: this.model }
		if (datasetRangeId == NOT_IN_ANY) {
			options.datasetRangeId = null
			options.datasetRangeType = 'not-in-any'
		} else if (datasetRangeId == BLANK) {
			options.datasetRangeId = null
			options.datasetRangeType = 'blank'
		} else {
			options.datasetRangeId = parseInt(datasetRangeId)
			options.datasetRangeType = 'standard'
		}

		let range = ChartDesignerBreakdown.datasetRangeBreakdownRange(options)

		this.model.ranges.add(range)
	},

	/**
	 * Opens the dialog to add a new calendar period breakdown range
	 */
	openNewCalendarPeriodRangeDialog: function () {
		let range = new ChartDesignerRange(
			ChartDesignerBreakdown.calendarPeriodBreakdownRange(this.model))

		let breakdown = this.model.clone()

		let editRangeView = new ChartsEditCalendarPeriodRangeView({
			model: range,
			breakdown,
			chartDesigner: this.chart,
			dialogTitle: 'charts.breakdown.addRange.numeric',
			isNewRange: true
		})

		appDisplay.dialog(editRangeView)

		this.listenToOnce(editRangeView, 'done', () => {
			this.chart.updateCalendarPeriodBreakdownRange({
				updatedBreakdown: breakdown,
				updatedRange: range
			})
		})
	},

	onClickAddRange: function () {
		if (this.model.isCalendarPeriodBreakdown()) {
			this.openNewCalendarPeriodRangeDialog()
		} else {
			// If the breakdown is on a dataset field, hide the Add button
			// and show the filtering select to add a new range
			this.toggleAddRangeButton(false)
			this.toggleAddDatasetRangeInput(true)
		}
	},

	onClickDelete: function () {
		this.chart.breakdowns.remove(this.model)
		this.chart.removeInvalidForecastOptions()

		// There can only be 2 breakdowns, so if there's still one left after
		// deletion, it has to be sortOrder 0
		this.chart.breakdowns.forEach(b => b.set('sortOrder', 0))
	},

	onClickEdit: function () {
		let clonedBreakdown = new ChartDesignerBreakdown(_.cloneDeep(this.model.toJSON()))

		let editView = new EditBreakdownView({
			model: clonedBreakdown,
			chart: this.chart,
			dataset: this.chart.getDatasetDatasource()
		})

		appDisplay.dialog(editView)

		this.listenToOnce(editView, 'done', updatedDesigner => {
			this.chart.set(updatedDesigner)
		})
	},

	onClickSwap: function (e) {
		// #43045 the swap button gets caught by the form-view and clicking it
		// causes the form-view to submit, closing the dialog
		e.preventDefault()

		// TODO rename some things if this is going to be generic
		if (this.model.get('sortOrder') == 0) {
			// TODO #26469 swap on first breakdown swaps with the y axis
			console.error('swap breakdown with y axis not yet implemented')
		} else {
			this.trigger('swap')
		}
	},

	removeRangeViews: function () {
		_.invokeMap(this.rangeViews, 'remove')
		this.rangeViews = []
	},

	inactive: function () {
		_.invokeMap(this.rangeViews, 'inactive')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.covertButtonTooltips, this.errorTooltip, this.sortSettingsView ], 'remove')
		this.removeRangeViews()
	}

})

function pseudoRangeOption (className, label) {
	let escapedLabel = _.escape(label)
	return `<span class="${className}">${escapedLabel}</span>`
}

export default BreakdownView

BreakdownView.__filename__ = 'charts/edit/breakdown-view.js'
